import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Badge, Button, Dialog, InlineError, Skeleton } from '../../components/ui';
import { useAuth } from '../auth/AuthProvider';
import {
  fetchLatestSnapshot,
  fetchOrganizationWithLatestReport,
  fetchSnapshotHistory,
  fetchSnapshotItems,
  fetchStockHistory,
  saveSnapshot,
} from './queries';
import type { InventoryItem, InventorySnapshot } from './queries';
import { InventoryHistory } from './InventoryHistory';
import { STOCK_LABELS, inventoryErrorMessage, stockLevel } from './parseReport';
import { burnRateOf, formatRate, formatRunway, runwayDaysFor, runwayLevel } from './burnRate';
import type { BurnRate } from './burnRate';
import type { MaterialCategory, ParsedReport } from './parseReport';
import { readCarnavalReport } from './readWorkbook';

/**
 * Materials inventory for the ES huddle.
 *
 * Shows the latest monthly snapshot with its report date stated plainly, so
 * nobody mistakes a month-old count for a live one. Uploading is a two-step
 * gesture on purpose: parse, show what was read, then save only on
 * confirmation. The preview is the whole safety mechanism — it is where a
 * shifted row or a misread number becomes visible before it becomes a record.
 */

export const inventoryKeys = {
  all: ['inventory'] as const,
  latest: (supplierId: string) => [...inventoryKeys.all, 'latest', supplierId] as const,
  items: (snapshotId: string) => [...inventoryKeys.all, 'items', snapshotId] as const,
};

/**
 * The supplier the Materials tab should land on: whoever has the most recent
 * report. Null while it loads, and null when nothing has ever been uploaded.
 */
export function useOrganizationWithLatestReport() {
  const { client } = useAuth();
  return useQuery({
    queryKey: [...inventoryKeys.all, 'latest-org'] as const,
    queryFn: () => fetchOrganizationWithLatestReport(client),
    retry: false,
  });
}

export function useLatestSnapshot(supplierOrganizationId: string) {
  const { client } = useAuth();
  return useQuery({
    queryKey: inventoryKeys.latest(supplierOrganizationId),
    queryFn: () => fetchLatestSnapshot(client, supplierOrganizationId),
    enabled: supplierOrganizationId !== '',
    retry: false,
  });
}

export function useSnapshotItems(snapshotId: string | null) {
  const { client } = useAuth();
  return useQuery({
    queryKey: inventoryKeys.items(snapshotId ?? ''),
    queryFn: () => fetchSnapshotItems(client, snapshotId ?? ''),
    enabled: snapshotId !== null,
    retry: false,
  });
}

/**
 * Every month on file for a supplier, newest first.
 *
 * Active revisions only. A superseded revision is a corrected report, and
 * offering both in a month picker asks somebody to choose between a number and
 * the same number after the factory recounted — there is only one right
 * answer and the picker should not pretend otherwise.
 */
export function useSnapshotList(supplierOrganizationId: string) {
  const { client } = useAuth();
  return useQuery({
    queryKey: [...inventoryKeys.all, 'list', supplierOrganizationId] as const,
    queryFn: () => fetchSnapshotHistory(client, supplierOrganizationId),
    enabled: supplierOrganizationId !== '',
    retry: false,
    select: (rows: InventorySnapshot[]) => rows.filter((row) => row.is_active),
  });
}

export function useStockHistory(supplierOrganizationId: string) {
  const { client } = useAuth();
  return useQuery({
    queryKey: [...inventoryKeys.all, 'history', supplierOrganizationId],
    queryFn: () => fetchStockHistory(client, supplierOrganizationId),
    enabled: supplierOrganizationId !== '',
    retry: false,
  });
}

export function useSaveSnapshot(supplierOrganizationId: string) {
  const { client, access } = useAuth();
  const queryClient = useQueryClient();
  const uploadedBy = access.status === 'approved' ? access.profile.id : '';
  return useMutation({
    mutationFn: (report: ParsedReport) =>
      saveSnapshot(client, {
        supplierOrganizationId,
        reportDate: report.reportDate,
        items: report.items,
        uploadedBy,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: inventoryKeys.all });
    },
  });
}

const CATEGORY_LABELS: Record<MaterialCategory | 'all', string> = {
  all: 'All materials',
  spool: 'Spools',
  bead: 'Beads',
  charm: 'Charms',
};

/** Spools come in fractions; showing 15.87 as 16 would overstate what is left. */
function formatQty(item: { stock_qty: number; category: string }): string {
  return item.category === 'spool' ? item.stock_qty.toFixed(2) : item.stock_qty.toLocaleString();
}

export function InventoryPanel({
  supplierOrganizationId,
  canUpload,
  audience = 'tap',
}: {
  supplierOrganizationId: string;
  /** Suppliers upload their own; TAP can upload on their behalf. */
  canUpload: boolean;
  /**
   * Whose screen this is.
   *
   * The panel was written for TAP and then reused, unchanged, in the supplier
   * portal — so a factory was shown OUR empty state: "Another supplier does
   * have one — check the picker above." That sentence tells a supplier other
   * suppliers exist, implies their stock is visible somewhere, and points at a
   * picker they do not have. It is our internal view leaking into theirs.
   *
   * Defaulting to 'tap' keeps the TAP call sites unchanged; the portal passes
   * 'supplier' and gets copy written for a factory.
   */
  audience?: 'tap' | 'supplier';
}) {
  const snapshot = useLatestSnapshot(supplierOrganizationId);
  const history = useStockHistory(supplierOrganizationId);
  const snapshots = useSnapshotList(supplierOrganizationId);
  const [category, setCategory] = useState<MaterialCategory | 'all'>('all');
  const [uploading, setUploading] = useState(false);
  /**
   * Which month is on screen. Null means "the newest", which is not the same
   * as pinning the newest one's id: a report uploaded while somebody has this
   * open should move them forward, not strand them on what used to be latest.
   */
  const [viewingId, setViewingId] = useState<string | null>(null);
  const months = snapshots.data ?? [];
  const viewing =
    viewingId === null ? snapshot.data : (months.find((row) => row.id === viewingId) ?? null);
  const items = useSnapshotItems(viewing?.id ?? null);
  // Only the newest month can be read as "what is on the shelf". An older one
  // is a record, and runway or restock alerts computed against it would be
  // answering a question about February in August.
  const isLatest = viewing === null || viewing === undefined || viewing.id === snapshot.data?.id;
  const reportElsewhere = useOrganizationWithLatestReport();
  // Only ever asked on TAP's screen. A factory has no picker to be pointed at
  // and no business knowing what another factory has filed.
  const otherSupplierHasReport =
    audience === 'tap' &&
    reportElsewhere.data !== null &&
    reportElsewhere.data !== undefined &&
    reportElsewhere.data !== supplierOrganizationId;

  const visible = (items.data ?? []).filter(
    (item) => category === 'all' || item.category === category,
  );

  return (
    <>
      <div className="inventory-header">
        <div>
          {snapshot.isPending || snapshot.data === undefined ? null : snapshot.data === null ? (
            <p className="task-panel-text">
              {/* Naming the supplier matters: the old wording read as "nobody
                  has uploaded anything", when usually somebody has — just for
                  a different supplier. */}
              {audience === 'supplier'
                ? 'No stock report uploaded yet. Upload one so TAP can see your material levels.'
                : 'No stock report for this supplier yet'}
              {otherSupplierHasReport
                ? '. Another supplier does have one — check the picker above.'
                : audience === 'supplier'
                  ? ''
                  : '.'}
            </p>
          ) : months.length > 1 ? (
            /* More than one month on file, so the date becomes a choice. A
               select rather than a list of chips: this grows by one every
               month and a year in it would be a wall of dates. */
            <label className="inventory-month-picker">
              <span className="drawer-autosave-note">Stock as of</span>
              <select
                className="ui-input ui-select"
                value={viewing?.id ?? ''}
                onChange={(event) =>
                  setViewingId(event.target.value === snapshot.data?.id ? null : event.target.value)
                }
              >
                {months.map((row) => (
                  <option key={row.id} value={row.id}>
                    {row.report_date}
                    {row.id === snapshot.data?.id ? ' (latest)' : ''}
                    {row.revision > 1 ? ` · revision ${row.revision}` : ''}
                  </option>
                ))}
              </select>
            </label>
          ) : (
            <p className="drawer-autosave-note">
              {/* The date the report covers, said plainly: a snapshot that
                  hides its age invites being read as live. */}
              Stock as of {snapshot.data.report_date}
              {snapshot.data.revision > 1 ? ` · revision ${snapshot.data.revision}` : ''}
            </p>
          )}
        </div>
        {canUpload ? (
          <Button variant="secondary" size="sm" onClick={() => setUploading(true)}>
            Upload stock report
          </Button>
        ) : null}
      </div>

      {snapshot.data === null || snapshot.data === undefined ? null : (
        <>
          <div className="inventory-filters" role="group" aria-label="Filter by material">
            {(['all', 'spool', 'bead', 'charm'] as const).map((key) => (
              <button
                key={key}
                type="button"
                className={`status-chip${category === key ? ' status-chip--on' : ''}`}
                aria-pressed={category === key}
                onClick={() => setCategory(key)}
              >
                {CATEGORY_LABELS[key]}
              </button>
            ))}
          </div>

          {items.isPending ? (
            <Skeleton lines={4} label="Loading stock" />
          ) : (
            <>
              {/* Alerts only on the newest month. Runway computed from an old
                  snapshot would be answering a question about February in
                  August — technically derived from real data and completely
                  the wrong answer. */}
              {isLatest ? (
                <RunoutAlerts items={items.data ?? []} history={history.data} />
              ) : (
                <p className="inventory-past-note">
                  An archived report. Alerts and runway are shown on the latest month only.
                </p>
              )}
              <StockTable items={visible} history={isLatest ? history.data : undefined} />
            </>
          )}

          <section className="inventory-section">
            <h3 className="project-category-heading">Stock over time</h3>
            <InventoryHistory history={history.data} items={items.data ?? []} />
          </section>
        </>
      )}

      {uploading ? (
        <UploadDialog
          supplierOrganizationId={supplierOrganizationId}
          onClose={() => setUploading(false)}
        />
      ) : null}
    </>
  );
}

/**
 * The line that answers the question somebody opened this screen with.
 *
 * Materials under 30 days are named rather than counted: "3 materials" makes
 * you hunt for which, and the whole point of a run-out warning is knowing what
 * to reorder.
 */
function RunoutAlerts({
  items,
  history,
}: {
  items: InventoryItem[];
  history: Map<string, { reportDate: string; stockQty: number }[]> | undefined;
}) {
  const critical = items.filter(
    (item) => runwayLevel(item.stock_qty, burnFor(history, item.sku)) === 'critical',
  );
  const out = items.filter((item) => item.stock_qty <= 0);
  const unknown = items.filter(
    (item) => runwayLevel(item.stock_qty, burnFor(history, item.sku)) === 'unknown',
  );

  if (critical.length === 0 && out.length === 0) {
    return unknown.length === items.length && items.length > 0 ? (
      <p className="drawer-autosave-note">
        {/* Said plainly rather than shown as healthy: one report cannot tell
            anyone how fast anything is being used. */}
        No burn rates yet — these need a second monthly report before runway can be worked out.
      </p>
    ) : null;
  }

  return (
    <div className="inventory-alerts">
      {out.length > 0 ? (
        <p>
          <strong>Out of stock:</strong> {out.map((item) => item.sku).join(', ')}
        </p>
      ) : null}
      {critical.length > 0 ? (
        <p>
          <strong>Under 30 days of supply:</strong> {critical.map((item) => item.sku).join(', ')}
        </p>
      ) : null}
    </div>
  );
}

function burnFor(
  history: Map<string, { reportDate: string; stockQty: number }[]> | undefined,
  sku: string,
): BurnRate {
  return burnRateOf(history?.get(sku) ?? []);
}

function StockTable({
  items,
  history,
}: {
  items: InventoryItem[];
  history: Map<string, { reportDate: string; stockQty: number }[]> | undefined;
}) {
  if (items.length === 0) {
    return <p className="task-panel-text">Nothing in this category.</p>;
  }
  return (
    <table className="ui-table">
      <thead>
        <tr>
          <th>SKU</th>
          <th>Material</th>
          <th>Unit</th>
          <th>Stock</th>
          <th>Used</th>
          <th>Runway</th>
          <th>Status</th>
        </tr>
      </thead>
      <tbody>
        {items.map((item) => {
          const level = stockLevel({ category: item.category, stockQty: item.stock_qty });
          const burn = burnFor(history, item.sku);
          const runway = runwayLevel(item.stock_qty, burn);
          return (
            <tr
              key={item.id}
              // The row itself carries the warning, not just a badge in the
              // last column. On a 56-row table a badge is something you find;
              // a tinted row is something you cannot miss.
              className={
                runway === 'critical' || level === 'depleted'
                  ? 'inventory-row--critical'
                  : runway === 'watch' || level === 'low'
                    ? 'inventory-row--watch'
                    : undefined
              }
            >
              <th scope="row">{item.sku}</th>
              <td>{item.description}</td>
              <td>{item.unit_of_measure}</td>
              <td>{formatQty(item)}</td>
              <td>
                {formatRate(burn.perDay, item.category)}
                {burn.confidence === 'provisional' ? (
                  <span className="deal-note-empty" title="Based on one month only">
                    {' '}
                    est.
                  </span>
                ) : null}
              </td>
              <td>
                {runway === 'unknown' ? (
                  <span className="deal-note-empty" title="Needs a second monthly report">
                    —
                  </span>
                ) : (
                  formatRunway(runwayDaysFor(item.stock_qty, burn))
                )}
              </td>
              <td>
                <div className="inventory-status-cell">
                  <Badge
                    tone={level === 'depleted' ? 'danger' : level === 'low' ? 'warning' : 'success'}
                  >
                    {STOCK_LABELS[level]}
                  </Badge>
                  {runway === 'critical' ? <Badge tone="danger">Under 30 days</Badge> : null}
                  {runway === 'watch' ? <Badge tone="warning">Under 60 days</Badge> : null}
                </div>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function UploadDialog({
  supplierOrganizationId,
  onClose,
}: {
  supplierOrganizationId: string;
  onClose: () => void;
}) {
  const save = useSaveSnapshot(supplierOrganizationId);
  const [report, setReport] = useState<ParsedReport | null>(null);
  const [error, setError] = useState('');
  const [reading, setReading] = useState(false);
  const [dragging, setDragging] = useState(false);

  async function take(file: File | undefined) {
    if (file === undefined) {
      return;
    }
    setError('');
    setReading(true);
    try {
      setReport(await readCarnavalReport(file));
    } catch (cause) {
      setReport(null);
      setError(inventoryErrorMessage(cause instanceof Error ? cause.message : ''));
    } finally {
      setReading(false);
    }
  }

  return (
    <Dialog open title="Upload stock report" onClose={onClose}>
      {error === '' ? null : <InlineError>{error}</InlineError>}

      {report === null ? (
        <div
          className={`inventory-drop${dragging ? ' inventory-drop--over' : ''}`}
          onDragOver={(event) => {
            event.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={(event) => {
            event.preventDefault();
            setDragging(false);
            void take(event.dataTransfer.files[0]);
          }}
        >
          <p>{reading ? 'Reading…' : 'Drop the .xlsx here'}</p>
          <label className="inventory-drop-browse">
            <span>or choose a file</span>
            <input
              type="file"
              accept=".xlsx"
              onChange={(event) => void take(event.target.files?.[0])}
            />
          </label>
        </div>
      ) : (
        <>
          {/* Nothing is saved until this has been read and confirmed. */}
          {report.dateWasGuessed ? (
            // Snapshots are immutable and filed by month, so a wrong date is
            // not a cosmetic problem — it files the whole report under the
            // wrong month permanently.
            <InlineError>
              No report date was found in the file, so today’s date is being used. Check this is the
              month you mean before saving.
            </InlineError>
          ) : null}
          <p className="drawer-autosave-note">
            Report date <strong>{report.reportDate}</strong>
            {report.dateWasGuessed ? ' (assumed)' : ''} · {report.items.length} materials. Check
            these before saving.
          </p>
          <div className="inventory-preview">
            <StockTable
              // A file being previewed has no history yet — it is not saved.
              history={undefined}
              items={report.items.slice(0, 8).map((item, index) => ({
                id: `preview-${index}`,
                snapshot_id: 'preview',
                sku: item.sku,
                description: item.description,
                unit_of_measure: item.unitOfMeasure,
                stock_qty: item.stockQty,
                category: item.category,
                position: index,
              }))}
            />
            {report.items.length > 8 ? (
              <p className="deal-note-empty">…and {report.items.length - 8} more.</p>
            ) : null}
          </div>
          <div className="deal-add-row">
            <Button
              onClick={() =>
                save.mutate(report, {
                  onSuccess: onClose,
                  onError: (cause: Error) => setError(inventoryErrorMessage(cause.message)),
                })
              }
              disabled={save.isPending}
            >
              {save.isPending ? 'Saving…' : 'Save snapshot'}
            </Button>
            <Button variant="secondary" onClick={() => setReport(null)}>
              Choose another file
            </Button>
          </div>
        </>
      )}
    </Dialog>
  );
}
